// components/private/order/PicCardView.tsx

import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import {
    Users,
    Building2,
    Banknote,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    Wallet,
    Search,
    RefreshCcw,
    Loader2,
    ListFilter,
    Download,
    Mail,
    Phone,
    Eye,
    CreditCard,
    X,
    Image as ImageIcon,
    PrinterCheck
} from "lucide-react";
import { axiosClient } from "@/lib/axiosClient";
import toast from "react-hot-toast";
import type { PicApiResponse, ParticipantData, SummaryData, MetaData } from "@/types/pic";

interface PicCardViewProps {
    activePeriodId: number;
    picName: string;
}

// Interface untuk Data Kartu Individu
interface IndividualCardData {
    registration_id: number;
    participant_name: string;
    package_name: string;
    registration_number: string;
    qr_code_url: string | null;
    status_pembayaran: string;
    expired_at: string;
    invoice_number: string;
}

const formatRupiah = (number: number) => {
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(number);
};

export const PicCardView = ({ activePeriodId, picName }: PicCardViewProps) => {
    const [participants, setParticipants] = useState<ParticipantData[]>([]);
    const [summary, setSummary] = useState<SummaryData | null>(null);
    const [meta, setMeta] = useState<MetaData | null>(null);

    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState("10");
    const [search, setSearch] = useState("");
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' }>({
        key: 'full_name',
        direction: 'asc'
    });

    // --- STATE UNTUK AKSI BULK (ZIP & EMAIL) ---
    const [isDownloadingZip, setIsDownloadingZip] = useState(false);
    const [isSendingEmail, setIsSendingEmail] = useState(false);

    // --- STATE UNTUK MODAL PREVIEW INDIVIDU ---
    const [previewModalOpen, setPreviewModalOpen] = useState(false);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewData, setPreviewData] = useState<IndividualCardData | null>(null);
    const [previewImage, setPreviewImage] = useState<string | null>(null);
    const [selectedParticipantId, setSelectedParticipantId] = useState<number | null>(null);

    const fetchGroupData = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axiosClient.get<PicApiResponse>(`/pic/participants`, {
                params: {
                    period_id: activePeriodId,
                    page: page,
                    per_page: perPage,
                    search: search,
                    sort_by: sortConfig.key,
                    sort_order: sortConfig.direction
                }
            });
            setParticipants(data.data);
            setSummary(data.summary);
            setMeta(data.meta);
        } catch (error) {
            toast.error("Gagal memuat data.");
            console.log(error);
        } finally {
            setLoading(false);
        }
    }, [activePeriodId, page, perPage, search, sortConfig]);

    useEffect(() => {
        const timeoutId = setTimeout(fetchGroupData, 500);
        return () => clearTimeout(timeoutId);
    }, [fetchGroupData]);

    // Bersihkan memory browser saat modal ditutup
    useEffect(() => {
        if (!previewModalOpen && previewImage) {
            window.URL.revokeObjectURL(previewImage);
            setPreviewImage(null);
            setPreviewData(null);
        }
    }, [previewModalOpen, previewImage]);

    // =========================================================================
    // FUNGSI 1: DOWNLOAD ZIP (BULK)
    // =========================================================================
    const handleDownloadZip = async () => {
        setIsDownloadingZip(true);
        const loadingToast = toast.loading("Menyiapkan ZIP Kartu...");
        try {
            const response = await axiosClient.get(`/pic/period/${activePeriodId}/download-cards-zip`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Kartu_Kolektif_${picName.replace(/\s+/g, '_')}.zip`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success("Berhasil mengunduh ZIP Kartu.", { id: loadingToast });
        } catch (error) {
            toast.error("Gagal mengunduh ZIP Kartu.", { id: loadingToast });
            console.error(error);
        } finally {
            setIsDownloadingZip(false);
        }
    };

    // =========================================================================
    // FUNGSI 2: KIRIM EMAIL ZIP KE PIC (BULK)
    // =========================================================================
    const handleSendEmailBulk = async () => {
        setIsSendingEmail(true);
        const loadingToast = toast.loading("Mengirim email ke akun Anda...");
        try {
            const { data } = await axiosClient.post(`/pic/period/${activePeriodId}/send-cards-email`);
            toast.success(data.message || "Email berhasil dikirim.", { id: loadingToast });
        } catch (error) {
            toast.error("Gagal mengirim email.", { id: loadingToast });
            console.error(error);
        } finally {
            setIsSendingEmail(false);
        }
    };

    // =========================================================================
    // FUNGSI 3: DOWNLOAD JPEG INDIVIDU
    // =========================================================================
    const handleDownloadIndividualCard = async (participantId: number, name: string) => {
        const loadingToast = toast.loading(`Mengunduh kartu ${name}...`);
        try {
            if (previewImage && participantId === selectedParticipantId) {
                const link = document.createElement('a');
                link.href = previewImage;
                link.setAttribute('download', `ID_Card_${name.replace(/\s+/g, '_')}.jpeg`);
                document.body.appendChild(link);
                link.click();
                link.remove();
                toast.success(`Kartu berhasil diunduh.`, { id: loadingToast });
                return;
            }

            const response = await axiosClient.get(`/pic/participants/${participantId}/registrations/period/${activePeriodId}/card-image`, {
                responseType: 'blob'
            });
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ID_Card_${name.replace(/\s+/g, '_')}.jpeg`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success(`Kartu berhasil diunduh.`, { id: loadingToast });
        } catch (error) {
            toast.error(`Gagal mengunduh kartu.`, { id: loadingToast });
            console.error(error);
        }
    };

    // =========================================================================
    // FUNGSI 4: BUKA MODAL PREVIEW INDIVIDU
    // =========================================================================
    const handleOpenPreview = async (participantId: number) => {
        setSelectedParticipantId(participantId);
        setPreviewModalOpen(true);
        setPreviewLoading(true);
        try {
            const { data } = await axiosClient.get(`/pic/participants/${participantId}/registrations/period/${activePeriodId}/card`);
            setPreviewData(data.data);

            const response = await axiosClient.get(`/pic/participants/${participantId}/registrations/period/${activePeriodId}/card-image`, {
                responseType: 'blob'
            });
            setPreviewImage(window.URL.createObjectURL(new Blob([response.data])));
        } catch (error) {
            toast.error("Gagal memuat pratinjau kartu.");
            setPreviewModalOpen(false);
            console.log(error);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleSort = (key: string) => {
        setSortConfig((prev) => ({
            key,
            direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
        }));
    };

    return (
        // ✅ BUNGKUS DENGAN FRAGMENT AGAR MODAL BISA KELUAR DARI MOTION.DIV
        <>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6 pb-10">

                {/* 1. TOP CARDS */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card className="bg-slate-900 border-slate-200 text-white shadow-xl rounded-2xl">
                        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                            <CardTitle className="text-[10px] font-black uppercase tracking-widest text-slate-500">Total Anggota</CardTitle>
                            <Users className="w-4 h-4 text-slate-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-3xl font-black tabular-nums">{summary?.total_participants ?? 0}</div>
                            <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">Peserta Terdaftar</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-white dark:bg-slate-900 border border-slate-200 shadow-lg rounded-2xl border-b-4 border-b-emerald-500">
                        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                            <CardTitle className="text-[10px] font-black uppercase tracking-widest text-slate-400">Total Settlement</CardTitle>
                            <Wallet className="w-4 h-4 text-emerald-500" />
                        </CardHeader>
                        <CardContent>
                            <div className="text-2xl font-black text-slate-900 dark:text-white tabular-nums tracking-tighter">
                                {formatRupiah(summary?.total_biaya ?? 0)}
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest font-bold">Akumulasi Kolektif</p>
                        </CardContent>
                    </Card>

                    <Card className="bg-white dark:bg-slate-900 border border-slate-200 shadow-lg rounded-2xl">
                        <CardHeader className="flex flex-row items-center justify-between pb-2 space-y-0">
                            <CardTitle className="text-[10px] font-black uppercase tracking-widest text-slate-400">Status Bayar</CardTitle>
                            <Banknote className="w-4 h-4 text-slate-400" />
                        </CardHeader>
                        <CardContent>
                            <div className="flex justify-between items-end">
                                <div>
                                    <span className="text-[9px] font-black uppercase text-rose-500">Unpaid</span>
                                    <div className="text-2xl font-black text-rose-600 tabular-nums">{summary?.total_unpaid ?? 0}</div>
                                </div>
                                <div className="w-px h-8 bg-slate-100 mx-2" />
                                <div className="text-right">
                                    <span className="text-[9px] font-black uppercase text-emerald-500">Paid</span>
                                    <div className="text-2xl font-black text-emerald-600 tabular-nums">{summary?.total_paid ?? 0}</div>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>

                {/* Info Cetak Kartu: anggota tidak perlu cetak mandiri, panitia yang akan memproses */}
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-600 via-blue-600 to-indigo-700 rounded-2xl p-5 shadow-lg shadow-blue-600/20">
                    <div className="absolute -top-6 -right-6 w-32 h-32 bg-white/10 rounded-full blur-2xl pointer-events-none" />
                    <div className="relative flex flex-col sm:flex-row sm:items-center gap-3.5">
                        <div className="shrink-0 w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center">
                            <PrinterCheck className="w-4 h-4 text-white" />
                        </div>
                        <div className="space-y-1.5">
                            <p className="text-sm font-black text-white flex items-center gap-1.5">
                                Informasi Cetak Kartu
                            </p>
                            <p className="text-xs text-blue-50/90 leading-relaxed font-medium">
                                Anggota grup Anda tidak perlu mencetak kartu ini secara mandiri. Panitia KONASPI XII akan memproses pencetakan kartu fisik dan menyediakannya saat check-in di lokasi acara. Jika ingin menyimpan salinan digital untuk dibagikan ke tim, tekan tombol <span className="font-bold text-white">"Email PIC"</span> di bawah untuk menerima seluruh kartu virtual dalam satu file ke inbox Anda.
                            </p>
                        </div>
                    </div>
                </div>

                {/* 2. CONTROLS BAR (Search, Limit, Export, Reload) */}
                <div className="flex flex-col xl:flex-row gap-4 items-center justify-between bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 shadow-sm">
                    <div className="relative w-full xl:w-1/3 group">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <Input
                            placeholder="Cari nama, email, institusi..."
                            className="pl-10 h-[48px] bg-slate-50 dark:bg-slate-950 border-slate-200 rounded-xl"
                            value={search}
                            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                        />
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2 w-full xl:w-auto font-bold">
                        {/* ✅ PERBAIKAN TINGGI TOMBOL: Menggunakan h-[48px] di semua elemen agar presisi */}
                        <Select value={perPage} onValueChange={(val) => { setPerPage(val); setPage(1); }}>
                            <SelectTrigger className="w-[130px] h-12 min-h-[48px] rounded-xl border border-slate-200 text-xs bg-white dark:bg-slate-900 shadow-sm box-border flex items-center">
                                <ListFilter size={14} className="mr-2 opacity-60" />
                                <SelectValue placeholder="Limit" />
                            </SelectTrigger>
                            <SelectContent className="font-bold">
                                <SelectItem value="10">10 Baris</SelectItem>
                                <SelectItem value="25">25 Baris</SelectItem>
                                <SelectItem value="50">50 Baris</SelectItem>
                                <SelectItem value="100">100 Baris</SelectItem>
                            </SelectContent>
                        </Select>

                        <Button
                            onClick={handleDownloadZip}
                            disabled={isDownloadingZip}
                            className="h-[48px] px-5 rounded-xl bg-slate-900 dark:bg-blue-600 hover:bg-slate-800 text-white font-black text-[10px] uppercase tracking-widest shadow-md transition-all active:scale-95"
                        >
                            {isDownloadingZip ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download size={16} className="mr-2" />}
                            Unduh ZIP
                        </Button>

                        <Button
                            onClick={handleSendEmailBulk}
                            disabled={isSendingEmail}
                            variant="outline"
                            className="h-[48px] px-5 rounded-xl bg-white dark:bg-slate-900 border-slate-200 text-slate-700 dark:text-slate-300 font-black text-[10px] uppercase tracking-widest shadow-sm transition-all active:scale-95"
                        >
                            {isSendingEmail ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Mail size={16} className="mr-2" />}
                            Email PIC
                        </Button>

                        <Button variant="outline" size="icon" onClick={fetchGroupData} className="h-[48px] w-[48px] rounded-xl bg-white dark:bg-slate-900 border-slate-200 shadow-sm" disabled={loading}>
                            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCcw size={16} className="text-slate-600" />}
                        </Button>
                    </div>
                </div>

                {/* 3. MAIN TABLE */}
                <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl overflow-hidden relative">
                    {loading && participants.length > 0 && (
                        <div className="absolute inset-0 bg-white/50 dark:bg-slate-900/50 backdrop-blur-[1px] z-10 flex items-center justify-center">
                            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
                        </div>
                    )}

                    <Table>
                        <TableHeader className="bg-slate-50 dark:bg-slate-800/50">
                            <TableRow className="hover:bg-transparent">
                                <TableHead className="w-[60px] text-center font-black text-[10px] uppercase tracking-widest text-slate-400">No</TableHead>
                                <TableHead className="cursor-pointer font-black text-[10px] uppercase tracking-widest text-slate-400" onClick={() => handleSort('full_name')}>
                                    Nama Partisipan
                                </TableHead>
                                <TableHead className="font-black text-[10px] uppercase tracking-widest text-slate-400">Kontak & Email</TableHead>
                                <TableHead className="font-black text-[10px] uppercase tracking-widest text-slate-400 hidden md:table-cell">Instansi</TableHead>
                                <TableHead className="font-black text-[10px] uppercase tracking-widest text-slate-400">Paket</TableHead>
                                <TableHead className="text-center font-black text-[10px] uppercase tracking-widest text-slate-400">Kelola</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {loading && participants.length === 0 ? (
                                [...Array(5)].map((_, i) => (
                                    <TableRow key={i}><TableCell colSpan={6} className="h-20 animate-pulse bg-slate-50/30" /></TableRow>
                                ))
                            ) : participants.length > 0 ? (
                                participants.map((p, index) => (
                                    <TableRow key={p.participant_id} className="border-slate-100 dark:border-slate-800 group">
                                        <TableCell className="text-center font-black text-slate-400 text-[10px]">
                                            {(meta ? (meta.current_page - 1) * meta.per_page : 0) + index + 1}
                                        </TableCell>
                                        <TableCell>
                                            <div className="font-bold text-slate-900 dark:text-white uppercase text-[11px] leading-none">{p.full_name}</div>
                                            <div className="text-[10px] font-mono font-bold text-blue-600 mt-2 uppercase tracking-tighter bg-blue-50 dark:bg-blue-900/30 w-fit px-1.5 rounded">
                                                {p.registration.registration_number}
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex flex-col gap-1">
                                                <div className="text-[10px] font-bold text-slate-600 dark:text-slate-300 flex items-center gap-1.5">
                                                    <Phone size={10} className="text-slate-400" /> {p.phone_mobile || "-"}
                                                </div>
                                                <div className="text-[10px] font-bold text-slate-400 lowercase flex items-center gap-1.5">
                                                    <Mail size={10} className="text-slate-400" /> {p.email}
                                                </div>
                                            </div>
                                        </TableCell>
                                        <TableCell className="hidden md:table-cell">
                                            <div className="text-[10px] text-slate-500 font-bold uppercase flex items-start gap-1.5">
                                                <Building2 size={11} className="mt-0.5 opacity-50 shrink-0" />
                                                <span className="leading-tight">{p.institution}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="text-[10px] font-black uppercase text-slate-700 dark:text-slate-300 leading-none">{p.registration.package_name}</div>
                                            <div className="mt-2 flex items-center gap-2">
                                                <div className={`h-1.5 w-1.5 rounded-full ${p.registration.payment_status === 'paid' ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-rose-500 shadow-[0_0_8px_rgba(244,63,94,0.5)]'}`} />
                                                <span className="text-[10px] font-bold text-emerald-600 font-mono tracking-tighter">{formatRupiah(p.registration.price)}</span>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex justify-center gap-2">
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    title="Lihat Pratinjau"
                                                    className="h-9 w-9 rounded-lg border-slate-200 text-slate-600 hover:bg-slate-900 dark:hover:bg-slate-700 hover:text-white shadow-sm transition-all"
                                                    onClick={() => handleOpenPreview(p.participant_id)}
                                                >
                                                    <Eye size={16} />
                                                </Button>
                                                <Button
                                                    variant="outline"
                                                    size="icon"
                                                    title="Unduh JPEG"
                                                    className="h-9 w-9 rounded-lg border-slate-200 text-slate-600 hover:bg-blue-600 dark:hover:bg-blue-600 hover:text-white shadow-sm transition-all"
                                                    onClick={() => handleDownloadIndividualCard(p.participant_id, p.full_name)}
                                                >
                                                    <Download size={16} />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))
                            ) : (
                                <TableRow>
                                    <TableCell colSpan={6} className="h-64 text-center">
                                        <AlertCircle className="mx-auto mb-3 text-slate-200" size={48} />
                                        <p className="font-black uppercase text-[10px] tracking-widest text-slate-400">Data tidak ditemukan</p>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>

                    {/* 4. PAGINATION FOOTER */}
                    {meta && meta.last_page > 0 && (
                        <div className="p-8 border-t bg-slate-50/50 dark:bg-slate-800/30 flex flex-col md:flex-row items-center justify-between gap-4">
                            <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                                Halaman <span className="text-slate-900 dark:text-white">{meta.current_page}</span> dari {meta.last_page} ({meta.total} Total Peserta)
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p - 1)}
                                    disabled={page === 1 || loading}
                                    className="h-10 px-6 rounded-xl border-slate-200 bg-white font-black text-[10px] uppercase shadow-sm dark:bg-slate-900"
                                >
                                    <ChevronLeft size={14} className="mr-1" /> Prev
                                </Button>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page === meta.last_page || loading}
                                    className="h-10 px-6 rounded-xl border-slate-200 bg-white font-black text-[10px] uppercase shadow-sm dark:bg-slate-900"
                                >
                                    Next <ChevronRight size={14} className="ml-1" />
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
            </motion.div>

            {/* ==================================================================== */}
            {/* ✅ MODAL PRATINJAU INDIVIDU DENGAN PORTAL */}
            {/* ==================================================================== */}
            {previewModalOpen && createPortal(
                <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-200">
                    <div className="bg-white dark:bg-slate-900 rounded-[2rem] shadow-2xl max-w-4xl w-full flex flex-col overflow-hidden animate-in zoom-in-95">
                        {/* Header Modal */}
                        <div className="flex justify-between items-center p-6 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                            <div>
                                <h3 className="font-black text-xl text-slate-900 dark:text-white flex items-center gap-2">
                                    <CreditCard className="w-5 h-5 text-blue-600" /> Pratinjau Kartu Peserta
                                </h3>
                            </div>
                            <Button variant="ghost" size="icon" onClick={() => setPreviewModalOpen(false)} className="rounded-full hover:bg-slate-200 dark:hover:bg-slate-700">
                                <X className="w-5 h-5" />
                            </Button>
                        </div>

                        {/* Body Modal */}
                        <div className="p-6 md:p-8 flex flex-col md:flex-row gap-8 items-center md:items-start max-h-[80vh] overflow-y-auto">
                            {previewLoading ? (
                                <div className="w-full flex flex-col items-center justify-center py-20 text-slate-400">
                                    <Loader2 className="animate-spin w-10 h-10 text-blue-600 mb-4" />
                                    <p className="text-sm font-medium">Memuat data kartu...</p>
                                </div>
                            ) : previewData && (
                                <>
                                    {/* Kolom Kiri: Gambar Kartu */}
                                    <div className="w-full md:w-[360px] shrink-0 border border-slate-200 dark:border-slate-700 rounded-[1.5rem] p-2 bg-slate-50 dark:bg-slate-800">
                                        {previewImage ? (
                                            <img src={previewImage} alt="Card Preview" className="w-full h-auto object-contain rounded-xl shadow-md border border-slate-200/50 dark:border-slate-700/50" />
                                        ) : (
                                            <div className="w-full aspect-[54/86] flex flex-col gap-2 items-center justify-center bg-slate-100 dark:bg-slate-900 rounded-xl text-slate-400 border border-slate-200 dark:border-slate-700">
                                                <ImageIcon className="w-8 h-8 opacity-50" />
                                                <span className="text-xs">Gambar tidak tersedia</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Kolom Kanan: Detail Informasi */}
                                    <div className="flex-1 space-y-6 w-full">
                                        <div className="space-y-1 border-b border-slate-100 dark:border-slate-800 pb-4">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Nama Partisipan</p>
                                            <p className="text-2xl font-black text-slate-900 dark:text-white leading-tight">{previewData.participant_name}</p>
                                        </div>

                                        <div className="space-y-1">
                                            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">Paket Kegiatan</p>
                                            <p className="font-bold text-slate-700 dark:text-slate-300">{previewData.package_name}</p>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 bg-slate-50 dark:bg-slate-800/50 p-4 rounded-xl border border-slate-100 dark:border-slate-700">
                                            <div>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Reg Number</p>
                                                <p className="font-mono font-bold text-xs text-slate-700 dark:text-slate-300">{previewData.registration_number}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Invoice Number</p>
                                                <p className="font-mono font-bold text-xs text-slate-700 dark:text-slate-300">{previewData.invoice_number}</p>
                                            </div>
                                            <div className="col-span-2 pt-2 border-t border-slate-200 dark:border-slate-700">
                                                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Status Pembayaran</p>
                                                <div className="flex items-center gap-2">
                                                    <div className={`h-2 w-2 rounded-full ${previewData.status_pembayaran === 'paid' ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                                                    <p className={`font-bold text-xs uppercase ${previewData.status_pembayaran === 'paid' ? 'text-emerald-600' : 'text-rose-600'}`}>
                                                        {previewData.status_pembayaran === 'paid' ? 'Lunas' : 'Belum Lunas'}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <Button
                                            onClick={() => handleDownloadIndividualCard(selectedParticipantId!, previewData.participant_name)}
                                            className="w-full h-12 mt-4 bg-blue-600 hover:bg-blue-700 text-white rounded-xl shadow-lg transition-all active:scale-95"
                                        >
                                            <Download className="w-4 h-4 mr-2" /> Unduh Kartu (JPEG)
                                        </Button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>,
                document.body // ✅ Ini Kunci Portalnya
            )}
        </>
    );
};